import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import type { Call } from "./api.js";
import { useApi } from "./context.js";
import { ErrorNote, formValues, label } from "./ui.js";

// Calling (design v0.8, C18a): the Call button beside a phone number, and
// the softphone panel. Dialing goes through the firm's telephony adapter
// (the fake in dev and the demo, which reaches nobody); a ringing inbound
// call shows who's calling with a link to their record; on hang-up the
// person confirms the outcome and notes, which logs the call on the record.
// Do-not-call blocks the button, with the reason.

const OUTCOMES = ["connected", "no_answer", "voicemail", "left_message", "busy", "wrong_number"];

export function CallButton({
  personType,
  personId,
}: {
  personType: "candidate" | "contact";
  personId: string;
}) {
  const api = useApi();
  const client = useQueryClient();
  const check = useQuery({
    queryKey: ["call-check", personType, personId],
    queryFn: () => api.callCheck(personType, personId),
  });
  const dial = useMutation({
    mutationFn: () => api.placeCall(personType, personId),
    onSuccess: () => client.invalidateQueries({ queryKey: ["calls-live"] }),
  });
  if (!check.data?.enabled || !check.data.number) return null;
  if (check.data.blocked)
    return (
      <span className="muted" title={check.data.reason ?? undefined}>
        {" "}
        · Don't call: {check.data.reason}
      </span>
    );
  return (
    <>
      {" "}
      <button
        type="button"
        className="link"
        disabled={dial.isPending}
        onClick={() => dial.mutate()}
      >
        Call
      </button>
      <ErrorNote error={dial.error} />
    </>
  );
}

function recordPath(c: Call) {
  if (!c.person_id) return null;
  return c.person_type === "candidate" ? `/candidates/${c.person_id}` : `/contacts/${c.person_id}`;
}

function CallCard({ c }: { c: Call }) {
  const api = useApi();
  const client = useQueryClient();
  const [notes, setNotes] = useState("");
  const done = () => client.invalidateQueries({ queryKey: ["calls-live"] });
  const answer = useMutation({ mutationFn: () => api.callAnswered(c.id), onSuccess: done });
  const hangUp = useMutation({ mutationFn: () => api.hangUp(c.id), onSuccess: done });
  const confirm = useMutation({
    mutationFn: (outcome: string) => api.confirmCall(c.id, { outcome, notes }),
    onSuccess: done,
  });
  const who = c.person_name ?? (c.direction === "inbound" ? c.from_number : c.to_number);
  const path = recordPath(c);
  return (
    <div className="softphone-call" role="status">
      <strong>
        {c.direction === "inbound" ? "Call from " : "Calling "}
        {path ? <Link to={path}>{who}</Link> : who}
      </strong>
      <small className="muted"> {label(c.status)}</small>
      {c.status === "ringing" || c.status === "dialing" ? (
        <div className="row">
          <button type="button" disabled={answer.isPending} onClick={() => answer.mutate()}>
            {c.direction === "inbound" ? "Answer" : "They answered"}
          </button>
          <button
            type="button"
            className="secondary"
            disabled={hangUp.isPending}
            onClick={() => hangUp.mutate()}
          >
            {c.direction === "inbound" ? "Decline" : "Hang up"}
          </button>
        </div>
      ) : c.status === "in_progress" ? (
        <div className="row">
          <button type="button" disabled={hangUp.isPending} onClick={() => hangUp.mutate()}>
            Hang up
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirm.mutate(formValues(e.currentTarget).outcome ?? "connected");
          }}
        >
          <label>
            Outcome{" "}
            <select name="outcome" defaultValue={c.outcome ?? "connected"}>
              {OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {label(o)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Notes{" "}
            <textarea
              name="notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <button type="submit" disabled={confirm.isPending}>
            {path ? "Log the call" : "Done"}
          </button>
        </form>
      )}
      <ErrorNote error={answer.error ?? hangUp.error ?? confirm.error} />
    </div>
  );
}

export function Softphone() {
  const api = useApi();
  const live = useQuery({
    queryKey: ["calls-live"],
    queryFn: api.liveCalls,
    // Only while calling is on: an inbound call shows within 20 seconds.
    refetchInterval: (q) => (q.state.data?.enabled ? 20_000 : false),
  });
  const calls = live.data?.data ?? [];
  if (!calls.length) return null;
  return (
    <aside className="softphone" aria-label="Calls">
      {calls.map((c) => (
        <CallCard key={c.id} c={c} />
      ))}
    </aside>
  );
}

// A record's calls (C18b), each with its review: the transcript of a
// recorded call and the Notetaker's notes.
export function RecentCalls({
  personType,
  personId,
}: {
  personType: "candidate" | "contact";
  personId: string;
}) {
  const api = useApi();
  const calls = useQuery({
    queryKey: ["record-calls", personType, personId],
    queryFn: () => api.recordCalls(personType, personId),
  });
  if (!calls.data?.data.length) return null;
  return (
    <section>
      <h2>Calls</h2>
      <ul>
        {calls.data.data.map((c) => (
          <li key={c.id}>
            <Link to={`/calls/${c.id}`}>
              {label(c.direction)} call, {new Date(c.started_at).toLocaleDateString()}
            </Link>
            {c.outcome ? ` · ${label(c.outcome)}` : ""}
            {c.recorded ? " · recorded" : ""}
            {c.transcript_id ? " · transcript" : ""}
          </li>
        ))}
      </ul>
    </section>
  );
}

// A candidate's desired rate and start, as a person confirmed them (C18b).
export function Expectations({ candidateId }: { candidateId: string }) {
  const api = useApi();
  const e = useQuery({
    queryKey: ["expectations", candidateId],
    queryFn: () => api.candidateExpectations(candidateId),
    enabled: Boolean(candidateId),
  });
  if (!e.data || (e.data.desired_rate == null && !e.data.available_from)) return null;
  return (
    <>
      {e.data.desired_rate != null ? (
        <>
          <dt>Desired rate</dt>
          <dd>
            ${e.data.desired_rate}/{e.data.desired_rate_unit ?? "hour"}
          </dd>
        </>
      ) : null}
      {e.data.available_from ? (
        <>
          <dt>Available from</dt>
          <dd>{e.data.available_from}</dd>
        </>
      ) : null}
    </>
  );
}
